// src/app/core/interceptors/error.interceptor.ts
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../../features/auth/services/auth.service';

/**
 * Interceptor que maneja los errores HTTP de forma centralizada
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'Ocurrió un error desconocido';

      if (error.error instanceof ErrorEvent) {
        // Error del lado del cliente
        errorMessage = `Error de conexión: ${error.error.message}`;
      } else {
        // Error del lado del servidor
        switch (error.status) {
          case 0:
            errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
            break;

          case 400:
            errorMessage = error.error?.message || 'Solicitud incorrecta. Verifica los datos enviados.';
            break;

          case 401:
            errorMessage = 'No autorizado. Tu sesión ha expirado.';
            authService.logout();
            router.navigate(['/login']);
            break;

          case 403:
            errorMessage = 'No tienes permisos para realizar esta acción.';
            break;

          case 404:
            errorMessage = error.error?.message || 'Recurso no encontrado.';
            break;

          case 409:
            errorMessage = error.error?.message || 'Conflicto: el recurso ya existe.';
            break;

          case 422:
            errorMessage = 'Error de validación. Verifica los datos enviados.';
            if (error.error?.errors) {
              errorMessage = Object.values(error.error.errors).join(', ');
            }
            break;

          case 429:
            errorMessage = 'Demasiadas solicitudes. Intenta de nuevo más tarde.';
            break;

          case 500:
            errorMessage = 'Error interno del servidor. Intenta de nuevo más tarde.';
            break;

          case 502:
            errorMessage = 'Servidor no disponible. Intenta de nuevo más tarde.';
            break;

          case 503:
            errorMessage = 'Servicio temporalmente no disponible. Intenta de nuevo más tarde.';
            break;

          default:
            errorMessage = `Error ${error.status}: ${error.message}`;
            if (error.error?.message) {
              errorMessage = error.error.message;
            }
        }
      }

      // Retornamos el error con información estructurada
      return throwError(() => ({
        status: error.status,
        message: errorMessage,
        originalError: error
      }));
    })
  );
};